import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  className?: string;
}

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}: EmptyStateProps) => {
  return (
    <Card className={className}>
      <CardContent className="flex flex-col items-center justify-center py-16 px-6 text-center">
        {/* Icon with background */}
        <div className="mb-6 rounded-full bg-muted/50 p-6">
          <Icon className="h-12 w-12 text-muted-foreground" strokeWidth={1.5} />
        </div>

        {/* Title */}
        <h3 className="mb-2 text-xl font-semibold text-foreground">{title}</h3>

        {/* Description */}
        <p className="mb-6 max-w-md text-sm text-muted-foreground">{description}</p>

        {/* Actions */}
        {(actionLabel || secondaryActionLabel) && (
          <div className="flex flex-col gap-3 sm:flex-row">
            {actionLabel && onAction && (
              <Button onClick={onAction} size="lg">
                {actionLabel}
              </Button>
            )}
            {secondaryActionLabel && onSecondaryAction && (
              <Button onClick={onSecondaryAction} variant="outline" size="lg">
                {secondaryActionLabel}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Compact variant for smaller sections
export const EmptyStateCompact = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
}: Omit<EmptyStateProps, 'secondaryActionLabel' | 'onSecondaryAction' | 'className'>) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="mb-4 rounded-full bg-muted/50 p-4">
        <Icon className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h4 className="mb-1 text-base font-medium text-foreground">{title}</h4>
      <p className="mb-4 max-w-sm text-sm text-muted-foreground">{description}</p>
      {actionLabel && onAction && (
        <Button onClick={onAction} variant="outline" size="sm">
          {actionLabel}
        </Button>
      )}
    </div>
  );
};
